<template>
  <div class="d-flex flex-column gap-5">
    <div class="d-flex flex-column gap-2">
      <span class="label typography-body-sm-bold">
        {{ t('yard.drawers.createTaskDrawer.labels.whatShouldBeChecked') }}
      </span>

      <v-btn-toggle
        data-testid="yard-task-check-subject-select"
        v-model="selectedSubject"
        density="compact"
        color="secondary"
        :disabled="disabledFields?.subject"
        mandatory
        divided>
        <v-btn class="border" :value="YardTaskCheckEntity.AssetContainer">Asset</v-btn>
        <v-btn class="border" :value="YardTaskCheckEntity.Spot">Spot</v-btn>
        <v-btn class="border" :value="YardTaskCheckEntity.SpotArea">Area</v-btn>
      </v-btn-toggle>
    </div>

    <div
      v-if="selectedSubject === YardTaskCheckEntity.AssetContainer"
      class="d-flex flex-column gap-2 required">
      <span class="label typography-body-sm-bold">
        {{ t('yard.drawers.createTaskDrawer.labels.whichAsset') }}
      </span>

      <yard-view-asset-select
        clearable
        include-waiting-assignment-assets
        include-assigned-assets
        persistent-hint
        v-model="selectedAsset"
        :disabled="disabledFields?.asset"
        :hide-details="Boolean(selectedAsset)"
        :hint="t('yard.drawers.createTaskDrawer.helpTexts.selectAnAssetToViewItsCurrentLocation')"
        :placeholder="t('yard.drawers.createTaskDrawer.placeholders.searchASpot')"
        :rules="[validateAsset]">
        <template #selected-asset="{ selectedAsset }">
          <small
            class="d-flex align-center gap-1 typography-body-sm line-height-normal text--color-text-secondary px-4">
            <v-icon size="x-small">{{ getIconByAssetStatus(selectedAsset) }}</v-icon>
            <span
              v-if="selectedAsset.isAssigned"
              v-html="
                t('yard.drawers.createTaskDrawer.helpTexts.locatedAt', {
                  area: selectedAsset.spotAreaName,
                  spot: selectedAsset.spotCode
                })
              "></span>
            <span v-else>{{ t('yard.drawers.createTaskDrawer.labels.waitingAssignment') }}</span>
          </small>
        </template>
      </yard-view-asset-select>
    </div>

    <div
      v-if="selectedSubject === YardTaskCheckEntity.Spot"
      class="d-flex flex-column gap-2 required">
      <span class="label typography-body-sm-bold">
        {{ t('yard.drawers.createTaskDrawer.labels.whichSpot') }}
      </span>

      <yard-view-spot-select
        clearable
        persistent-hint
        include-assigned-spots
        include-closed-spots
        include-reserved-spots
        show-spot-status
        v-model="selectedSpot"
        :disabled="disabledFields?.spot"
        :hide-details="Boolean(selectedSpot)"
        :hint="t('yard.drawers.createTaskDrawer.helpTexts.selectAnAssetToViewItsCurrentLocation')"
        :placeholder="t('yard.drawers.createTaskDrawer.placeholders.searchASpot')"
        :rules="[validateSpot]">
        <template #selected-spot="{ selectedSpot }">
          <small
            class="d-flex align-baseline gap-1 typography-body-sm line-height-normal text--color-text-secondary px-4">
            <v-icon size="x-small">{{ getIconBySpotStatus(selectedSpot.status) }}</v-icon>
            <span
              v-html="
                t('yard.drawers.createTaskDrawer.helpTexts.spotIs', { status: selectedSpot.status })
              "></span>
          </small>
        </template>
      </yard-view-spot-select>
    </div>

    <div
      v-if="selectedSubject === YardTaskCheckEntity.SpotArea"
      class="d-flex flex-column gap-2 required">
      <span class="label typography-body-sm-bold">
        {{ t('yard.drawers.createTaskDrawer.labels.whichArea') }}
      </span>

      <yard-view-area-select
        clearable
        persistent-hint
        v-model="selectedArea"
        :disabled="disabledFields?.area"
        :hide-details="Boolean(selectedArea)"
        :placeholder="t('yard.drawers.createTaskDrawer.placeholders.searchAnArea')"
        :rules="[validateArea]"
        :hint="t('yard.drawers.createTaskDrawer.helpTexts.selectAnAreaToViewItsCurrentStatus')">
        <template #selected-areas>
          <div class="d-flex flex-row gap-4 align-center px-4">
            <div
              v-for="stat in areaStatistics"
              :key="stat.label"
              class="d-flex flex-row gap-1 align-center">
              <v-icon size="x-small">{{ stat.icon }}</v-icon>
              <span class="typography-body-sm-bold line-height-normal">{{ stat.count }}</span>
              <span
                class="typography-body-sm line-height-normal text-lowercase text--color-text-secondary">
                {{ stat.label }}
              </span>
            </div>
          </div>
        </template>
      </yard-view-area-select>
    </div>

    <div class="d-flex flex-column gap-2 required">
      <span class="label typography-body-sm-bold">
        {{ t('yard.drawers.createTaskDrawer.labels.whatsTheTaskCategory') }}
      </span>

      <v-autocomplete
        :key="selectedSubject"
        clearable
        data-testid="yard-task-category-select"
        color="primary"
        density="compact"
        hide-details="auto"
        item-title="name"
        item-value="id"
        variant="outlined"
        v-model="selectedCategory"
        :disabled="disabledFields?.category"
        :items="categories"
        :persistent-hint="!selectedCategory"
        :placeholder="t('yard.drawers.createTaskDrawer.placeholders.selectAnOption')"
        :rules="[validateCategory]">
        <template #message="{ message }">
          <small class="typography-body-sm">
            {{ message }}
          </small>
        </template>
      </v-autocomplete>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { YardTaskCheckEntity } from '@nova/core';
import YardViewSpotSelect from '@/modules/yard/components/Selects/YardViewSpotSelect.vue';
import YardViewAreaSelect from '@/modules/yard/components/Selects/YardViewAreaSelect.vue';
import YardViewAssetSelect from '@/modules/yard/components/Selects/YardViewAssetSelect.vue';
import { getAvailableCategoriesForEntity, getCategoryLabelTranslationKey } from '@nova/core';
import useYard from '@/modules/yard/composables/useYard';

import {
  isAssetValid,
  isSpotValid,
  isAreaValid,
  isCategoryValid
} from '@/modules/yard/components/YardTasksCreateTaskCheckDetails.helpers';

export default defineComponent({
  name: 'YardTasksCreateTaskCheckDetails',
  // keeping the input event just for "consistency" with existing components; I don't think it makes sense in such context though
  emits: ['input', 'change'],
  components: {
    YardViewSpotSelect,
    YardViewAreaSelect,
    YardViewAssetSelect
  },
  props: {
    subject: {
      type: String,
      required: false,
      default: null
    },
    spot: {
      type: String,
      required: false,
      default: null
    },
    area: {
      type: String,
      required: false,
      default: null
    },
    asset: {
      type: String,
      required: false,
      default: null
    },
    category: {
      type: String,
      required: false,
      default: null
    },
    disabledFields: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  setup(props, { emit }) {
    const yardStore = useYard();
    const { t } = useI18n();

    const selectedSubject = ref(props.subject ?? YardTaskCheckEntity.AssetContainer);
    const selectedSpot = ref(props.spot);
    const selectedArea = ref(props.area);
    const selectedAsset = ref(props.asset);
    const selectedCategory = ref(props.category);

    const categories = computed(() => {
      return getAvailableCategoriesForEntity(selectedSubject.value).map(category => ({
        id: category,
        name: t(getCategoryLabelTranslationKey(category))
      }));
    });

    const areaStatistics = computed(() => {
      const stats = {
        total: {
          label: t('yard.drawers.createTaskDrawer.labels.totalSpots'),
          count: 0,
          icon: 'mdi-view-column'
        },
        occupied: {
          label: t('core.yard.spotStatuses.assigned'),
          count: 0,
          icon: getIconBySpotStatus('assigned')
        },
        open: {
          label: t('core.yard.spotStatuses.open'),
          count: 0,
          icon: getIconBySpotStatus('open')
        },
        closed: {
          label: t('core.yard.spotStatuses.closed'),
          count: 0,
          icon: getIconBySpotStatus('closed')
        }
      };

      if (selectedArea.value) {
        const spots = yardStore.getSpotsByArea(selectedArea.value);

        spots.forEach(spot => {
          stats.total.count++;
          if (yardStore.isSpotOpen(spot)) {
            stats.open.count++;
          } else if (yardStore.isSpotClosed(spot)) {
            stats.closed.count++;
          } else if (yardStore.isSpotAssigned(spot)) {
            stats.occupied.count++;
          }
        });
      }

      return Object.values(stats);
    });

    const getIconBySpotStatus = status => {
      if (status === 'closed') {
        return 'mdi-close';
      } else if (status === 'assigned') {
        return 'mdi-view-column-outline';
      } else if (status === 'open') {
        return 'mdi-check';
      }

      return 'mdi-view-column';
    };

    const getIconByAssetStatus = asset => {
      if (asset.isAssigned) {
        return 'mdi-map-marker-outline';
      }

      return 'mdi-clock-outline';
    };

    const validateAsset = value => {
      if (selectedSubject.value === YardTaskCheckEntity.AssetContainer && isAssetValid(value)) {
        return true;
      }

      return t('yard.drawers.createTaskDrawer.errors.assetIsRequired');
    };

    const validateSpot = value => {
      if (selectedSubject.value === YardTaskCheckEntity.Spot && isSpotValid(value)) {
        return true;
      }

      return t('yard.drawers.createTaskDrawer.errors.spotIsRequired');
    };

    const validateArea = value => {
      if (selectedSubject.value === YardTaskCheckEntity.SpotArea && isAreaValid(value)) {
        return true;
      }

      return t('yard.drawers.createTaskDrawer.errors.areaIsRequired');
    };

    const validateCategory = value => {
      if (isCategoryValid(value)) {
        return true;
      }

      return t('yard.drawers.createTaskDrawer.errors.categoryIsRequired');
    };

    watch(selectedSubject, newVal => {
      selectedCategory.value = null;

      if (newVal === YardTaskCheckEntity.AssetContainer) {
        selectedAsset.value = props.asset;
        selectedSpot.value = null;
        selectedArea.value = null;
      } else if (newVal === YardTaskCheckEntity.Spot) {
        selectedAsset.value = null;
        selectedSpot.value = props.spot;
        selectedArea.value = null;
      } else if (newVal === YardTaskCheckEntity.SpotArea) {
        selectedAsset.value = null;
        selectedSpot.value = null;
        selectedArea.value = props.area;
      }
    });

    watch(
      [selectedSubject, selectedAsset, selectedSpot, selectedArea, selectedCategory],
      ([
        newselectedSubject,
        newSelectedAsset,
        newSelectedSpot,
        newSelectedArea,
        newSelectedCategory
      ]) => {
        const eventData = {
          subject: newselectedSubject,
          asset: newSelectedAsset,
          spot: newSelectedSpot,
          area: newSelectedArea,
          category: newSelectedCategory
        };

        emit('input', eventData);
        emit('change', eventData);
      }
    );

    return {
      t,
      selectedSubject,
      selectedAsset,
      selectedSpot,
      selectedArea,
      selectedCategory,
      categories,
      areaStatistics,
      getIconBySpotStatus,
      getIconByAssetStatus,
      validateAsset,
      validateSpot,
      validateArea,
      validateCategory,
      YardTaskCheckEntity
    };
  }
});
</script>

<style lang="scss" scoped>
.line-height-normal {
  line-height: normal !important;
}
</style>
